import { Banner, Flex } from '@iheartjane/reefer';
import type { CartBrandDiscount, ReservationMode } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';

import { getPickupMinAmount } from '@jane/monolith/utils/minimumPickupOrder';
import { QualifiedBundleBanner } from '@jane/shared-ecomm/components';
// eslint-disable-next-line no-restricted-imports
import type { Store } from '@jane/shared/models';
import type { DeliveryValidation } from '@jane/shared/types';
import type { CartLimitPolicyReport } from '@jane/shared/util';

import { CartAlertsWrapper } from './cartDrawerAlerts.styles';
import { CartLimitPolicyAlert } from './cartLimitPolicyAlert';

export interface CartValidationAlertProps {
  brandDiscounts?: CartBrandDiscount[];
  cartReport: CartLimitPolicyReport | null;
  deliveryLabel: string;
  deliveryValidationData: DeliveryValidation | null;
  isHeadlessWide?: boolean;
  isPendingCart?: boolean;
  minimumDeliveryOrderMet: boolean;
  minimumPickupOrderMet: boolean;
  onDismissBrandDiscountBanner?: (id: number) => void;
  pickupLabel: string;
  reservationAlert?: string | null;
  reservationMode: ReservationMode;
  store: Store;
}

/**
 * This component is used to display validation messages
 *
 * This component checks if the cart meets requirements and accepts reservation alert message
 * to render alerts together
 */

export const CartValidationAlert = ({
  brandDiscounts = [],
  cartReport,
  deliveryLabel,
  deliveryValidationData,
  isHeadlessWide,
  isPendingCart,
  pickupLabel,
  minimumDeliveryOrderMet,
  minimumPickupOrderMet,
  onDismissBrandDiscountBanner,
  reservationAlert,
  reservationMode,
  store,
}: CartValidationAlertProps) => {
  let validationMessage: string | undefined = undefined;

  const cartReportOver = cartReport ? cartReport.over : false;

  if (reservationMode === 'pickup' && !minimumPickupOrderMet) {
    const pickupMinimum = getPickupMinAmount(store) || 0;

    validationMessage = `${
      store.name
    } requires a minimum ${pickupLabel} purchase of ${formatCurrency(
      pickupMinimum
    )} before fees`;
  }

  if (
    reservationMode === 'delivery' &&
    !minimumDeliveryOrderMet &&
    deliveryValidationData?.minimum
  ) {
    validationMessage = `${
      store.name
    } requires a minimum ${deliveryLabel} purchase of ${formatCurrency(
      deliveryValidationData.minimum
    )} before fees`;
  }

  const hasValidationError =
    !!validationMessage || !!reservationAlert || cartReportOver;

  const hasBrandDiscounts = brandDiscounts.length > 0;

  if (!hasValidationError && !hasBrandDiscounts) return null;

  return (
    <CartAlertsWrapper
      isHeadlessWide={isHeadlessWide}
      isPendingCart={isPendingCart}
    >
      {reservationAlert && (
        <Banner label={reservationAlert} variant="error" full />
      )}

      {validationMessage && (
        <Banner label={validationMessage} variant="error" full />
      )}

      {cartReportOver && <CartLimitPolicyAlert report={cartReport} />}

      {hasBrandDiscounts && (
        <Flex flexDirection="column" gap={16}>
          {brandDiscounts.map((special) => (
            <QualifiedBundleBanner
              key={special.brand_special_id}
              onDismiss={() =>
                onDismissBrandDiscountBanner &&
                onDismissBrandDiscountBanner(special.brand_special_id)
              }
              specialTitle={special.special_title}
            />
          ))}
        </Flex>
      )}
    </CartAlertsWrapper>
  );
};
